import * as XLSX from "xlsx";
import { ValidationResult, ProviderRow, ValidationError } from "./types";

const validatePhone = (value: string): string => {
    if (!value) return "";
    const cleaned = value.replace(/[^\d+\-\n;]/g, "");
    return cleaned.split(/[\n;]/).map(part => part.trim()).filter(Boolean).join(";").substring(0, 10);
};

const validateTaxId = (value: string): string => {
    if (!value) return "";
    const cleaned = value.replace(/[^\d+\-\n;]/g, "");
    return cleaned.split(/[\n;]/).map(part => part.trim()).filter(Boolean).join(";").substring(0, 10);
};

const validateFax = (value: string): number | null => {
    if (!value) return null;
    const cleaned = value.replace(/\D/g, "");
    return cleaned ? parseInt(cleaned, 10) : null;
};

const validatePayableDays = (value: string): number | null => {
    if (!value) return null;
    const cleaned = value.replace(/\D/g, "");
    return cleaned ? parseInt(cleaned, 10) : null;
};

export const validateExcelFile = async (file: File): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

                if (jsonData.length < 3) {
                    reject(new Error("File phải có ít nhất 3 dòng (headers ở dòng 3)"));
                    return;
                }

                // Get headers from row 3 (index 2)
                // Row 1: blank/instructions (skip)
                // Row 2: Vietnamese headers (for user reference, skip)
                // Row 3: English headers (parse this)
                const headerRow = jsonData[2];
                if (!headerRow || headerRow.length === 0) {
                    reject(new Error("Không tìm thấy headers ở dòng 3"));
                    return;
                }

                const headers: { [key: number]: string } = {};
                const fieldMap: { [key: string]: number } = {};
                const contactListFields: { [key: string]: number } = {};

                headerRow.forEach((header: any, colIdx: number) => {
                    if (header) {
                        const headerStr = String(header).trim();
                        headers[colIdx] = headerStr;
                        if (headerStr.startsWith("contact_list.")) {
                            const fieldName = headerStr.split(".", 2)[1];
                            contactListFields[fieldName] = colIdx;
                        } else {
                            fieldMap[headerStr] = colIdx;
                        }
                    }
                });

                const rows: ProviderRow[] = [];
                const errors: ValidationError[] = [];
                let originalRowCount = 0;

                // Process data starting from row 4 (index 3)
                for (let rowIdx = 3; rowIdx < jsonData.length; rowIdx++) {
                    const row = jsonData[rowIdx];
                    const rowNumber = rowIdx + 1; // Excel row number (1-based)

                    // Skip empty rows
                    if (!row || row.every((cell: any) => cell === null || cell === undefined || String(cell).trim() === "")) {
                        continue;
                    }
                    
                    originalRowCount++;

                    const rowErrors: ValidationError[] = [];
                    const providerData: any = {};

                    // Process regular fields
                    Object.entries(fieldMap).forEach(([field, colIdx]) => {
                        try {
                            let value = row[colIdx];
                            if (value === null || value === undefined) {
                                return;
                            }

                            if (typeof value !== "string") {
                                value = String(value);
                            }

                            // Apply field-specific validation
                            if (field === "phone") {
                                value = validatePhone(value);
                                providerData[field] = value;
                            } else if (field === "tax_id") {
                                value = validateTaxId(value);
                                providerData[field] = value;
                            } else if (field === "fax") {
                                const faxValue = validateFax(value);
                                if (faxValue !== null) {
                                    providerData[field] = faxValue;
                                }
                            } else if (field === "payable_days") {
                                const payableDaysValue = validatePayableDays(value);
                                if (payableDaysValue !== null) {
                                    providerData[field] = payableDaysValue;
                                }
                            } else if (field === "name" || field === "email") {
                                // Truncate to 255 characters
                                value = value.substring(0, 255);
                                providerData[field] = value;
                            } else if (field === "provider_code" || field === "address" || field === "note" || 
                                      field === "bank_account_name" || field === "bank_name" || field === "bank_account_number") {
                                // Normalize whitespace
                                value = value.replace(/\s+/g, " ").trim();
                                providerData[field] = value;
                            } else {
                                // For other fields, just assign the value
                                providerData[field] = value;
                            }
                        } catch (e: any) {
                            rowErrors.push({
                                row: rowNumber,
                                field,
                                error: `Lỗi xử lý trường: ${e.message}`,
                                data: row[colIdx],
                            });
                        }
                    });

                    // Process contact list fields
                    const contact: any = {};
                    Object.entries(contactListFields).forEach(([field, colIdx]) => {
                        try {
                            let value = row[colIdx];
                            if (value === null || value === undefined) {
                                return;
                            }

                            if (typeof value !== "string") {
                                value = String(value);
                            }

                            if (field === "phone") {
                                value = validatePhone(value);
                            }

                            contact[field] = value;
                        } catch (e: any) {
                            rowErrors.push({
                                row: rowNumber,
                                field: `contact_list.${field}`,
                                error: `Lỗi xử lý trường: ${e.message}`,
                                data: row[colIdx],
                            });
                        }
                    });

                    // Validate required fields
                    if (!providerData.name || String(providerData.name).trim() === "") {
                        rowErrors.push({
                            row: rowNumber,
                            field: "name",
                            error: "Tên nhà cung cấp là bắt buộc",
                        });
                    }

                    // Set defaults
                    if (providerData.is_internal === undefined) {
                        providerData.is_internal = false;
                    }
                    // payable_days can be null, so only set if not present
                    if (!("payable_days" in providerData)) {
                        providerData.payable_days = null;
                    }

                    // Add contact list if present
                    if (Object.keys(contact).length > 0) {
                        // Only add non-empty contact fields
                        const cleanContact: any = {};
                        Object.entries(contact).forEach(([key, val]) => {
                            if (val !== null && val !== undefined && String(val).trim() !== "") {
                                cleanContact[key] = val;
                            }
                        });
                        
                        if (Object.keys(cleanContact).length > 0) {
                            cleanContact.is_default = true;
                            providerData.contact_list = [
                                {
                                    id: 1,
                                    contact: cleanContact,
                                },
                            ];
                        } else {
                            providerData.contact_list = [];
                        }
                    } else {
                        providerData.contact_list = [];
                    }

                    const rowToAdd: ProviderRow = {
                        rowNumber,
                        data: providerData,
                        isValid: rowErrors.length === 0,
                        errors: rowErrors,
                    };

                    rows.push(rowToAdd);
                    errors.push(...rowErrors);
                }

                // Check for duplicate providers within the file
                const providerGroups: { [key: string]: ProviderRow } = {};

                rows.forEach((row) => {
                    const providerData = row.data;
                    // Use provider_code as primary identifier, fallback to name
                    const providerCode = providerData.provider_code;
                    const name = providerData.name;
                    const identifier = (providerCode && String(providerCode).trim()) || (name && String(name).trim()) || null;

                    // If no identifier, use row number as fallback to ensure row is included
                    if (!identifier || identifier === "") {
                        // Row without identifier - add error and include it anyway
                        row.errors.push({
                            row: row.rowNumber,
                            field: "name",
                            error: "Nhà cung cấp phải có tên hoặc mã nhà cung cấp",
                        });
                        // Also update isValid since we added an error
                        row.isValid = false;
                        // Use row number as identifier to ensure it's included
                        const fallbackKey = `_no_identifier_${row.rowNumber}`;
                        providerGroups[fallbackKey] = row;
                        return;
                    }

                    const identifierKey = String(identifier).trim().toLowerCase();

                    if (identifierKey in providerGroups) {
                        // Found duplicate - merge contact lists
                        const existingRow = providerGroups[identifierKey];
                        
                        // Add warning to the first occurrence row (the one being kept)
                        existingRow.errors.push({
                            row: existingRow.rowNumber,
                            field: providerData.provider_code ? "provider_code" : "name",
                            error: `Nhà cung cấp trùng lặp với dòng ${row.rowNumber}. Các thông tin liên hệ sẽ được gộp lại.`,
                            data: identifier,
                        });

                        // Merge contact lists
                        const existingContacts = existingRow.data.contact_list || [];
                        const newContacts = providerData.contact_list || [];

                        // Get highest ID from existing contacts
                        let maxId = 0;
                        const existingContactDetails: string[] = [];
                        existingContacts.forEach((contactEntry: any) => {
                            if (contactEntry && contactEntry.id) {
                                maxId = Math.max(maxId, contactEntry.id);
                                if (contactEntry.contact) {
                                    existingContactDetails.push(JSON.stringify(contactEntry.contact));
                                }
                            }
                        });

                        // Add new contacts that don't already exist
                        let nextId = maxId + 1;
                        newContacts.forEach((newContactEntry: any) => {
                            if (newContactEntry && newContactEntry.contact) {
                                const contactStr = JSON.stringify(newContactEntry.contact);
                                if (!existingContactDetails.includes(contactStr)) {
                                    // New contacts should not be default (first contact in existing is already default)
                                    if (newContactEntry.contact) {
                                        newContactEntry.contact.is_default = false;
                                    }
                                    newContactEntry.id = nextId;
                                    existingContacts.push(newContactEntry);
                                    existingContactDetails.push(contactStr);
                                    nextId++;
                                }
                            }
                        });

                        // Ensure only the first contact is default
                        if (existingContacts.length > 0) {
                            existingContacts.forEach((contactEntry: any, index: number) => {
                                if (contactEntry && contactEntry.contact) {
                                    contactEntry.contact.is_default = index === 0;
                                }
                            });
                        }

                        existingRow.data.contact_list = existingContacts;
                    } else {
                        providerGroups[identifierKey] = row;
                    }
                });

                // Update rows to use deduplicated data
                const uniqueRows = Object.values(providerGroups);
                
                // Recalculate errors from uniqueRows to ensure consistency
                const finalErrors: ValidationError[] = [];
                uniqueRows.forEach(row => {
                    if (row.errors && Array.isArray(row.errors)) {
                        finalErrors.push(...row.errors);
                    }
                });

                resolve({ rows: uniqueRows, errors: finalErrors, originalRowCount });
            } catch (e: any) {
                reject(new Error(`Lỗi đọc file: ${e.message}`));
            }
        };

        reader.onerror = () => {
            reject(new Error("Không thể đọc file"));
        };

        reader.readAsArrayBuffer(file);
    });
};

